import { useEffect, useMemo, useState } from "react";

import { MaterialType } from "../../../types/materials";
import { getKeys } from "../../../utils/object";
import { getSearchParams } from "../../../utils/search-params";
import {
  EventsFiltersForm,
  EventsTimeInterval,
  EventsTypeFilterValue,
  EventType,
} from "../../events/types";
import { MaterialOption } from "../../forms/forms-fields/materials-form-fields";
import { FORM_DEFAULT_VALUES } from "./form-default-values";

export function useEventsFilters() {
  const searchParams = getSearchParams();

  const defaultValues: EventsFiltersForm = useMemo(() => {
    const materialQuery = searchParams?.get("materials")?.split(",") ?? [];
    const eventTypeQuery = searchParams?.get("eventType")?.split(",") ?? [];

    return {
      timeInterval:
        (searchParams?.get("timeInterval") as EventsTimeInterval) ??
        EventsTimeInterval.FUTURE_EVENTS,
      country: searchParams?.get("country") ?? "",
      search: searchParams?.get("search") ?? "",
      materials: materialQuery.reduce<Record<MaterialOption, boolean>>(
        (acc, materialQueryValue) => {
          const material = materialQueryValue as MaterialType;
          if (Object.values(MaterialType).includes(material)) {
            acc[material] = true;
          }
          return acc;
        },
        { ...FORM_DEFAULT_VALUES.materials }
      ),
      eventType: eventTypeQuery.reduce<EventsTypeFilterValue>(
        (acc, type) => {
          if (type === EventType.DIGITAL || type === EventType.IN_PERSON) {
            acc[type] = true;
          }
          return acc;
        },
        { ...FORM_DEFAULT_VALUES.eventType }
      ),
    };
  }, [searchParams]);

  const [appliedFilters, setAppliedFilters] = useState<EventsFiltersForm>(defaultValues);

  const availableParams = useMemo(
    () =>
      getKeys(appliedFilters).reduce<Partial<Record<keyof EventsFiltersForm, string>>>(
        (acc, filterKey) => {
          let queryValue = "";
          const filterValue = appliedFilters[filterKey];
          if (typeof filterValue === "object") {
            queryValue = getKeys(filterValue)
              .reduce<string[]>(
                (subFilterKeys, subKey) =>
                  filterValue[subKey] ? [...subFilterKeys, subKey] : subFilterKeys,
                []
              )
              .join(",");
          } else {
            queryValue = filterValue;
          }
          if (queryValue) {
            acc[filterKey] = queryValue;
          }
          return acc;
        },
        {}
      ),
    [appliedFilters]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams();
      getKeys(availableParams).forEach((key) => {
        searchParams.set(key, availableParams[key] ?? "");
      });

      const path = [`${window.location.origin}${window.location.pathname}`, searchParams.toString()]
        .filter(Boolean)
        .join("?");
      window.history.pushState({ path }, "", path);
    }
  }, [availableParams]);

  return useMemo(
    () => ({
      appliedFilters,
      setAppliedFilters,
    }),
    [appliedFilters]
  );
}
